import { useState, useEffect, useRef, useMemo } from 'react';
import { collection, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { db } from '../../config/firebaseConfig';
import { useUserProfile } from '../../contexts/UserProfileContext';
import { useTranslation } from '../../i18n/LanguageContext';

// Projesi silinmiş tarifeler için ortak etiket. Ham doküman kimliği yerine bu
// gösterilir; böylece proje listesinde tek bir okunabilir girdi olarak toplanır.
const DELETED_PROJECT_LABEL = 'Silinmiş proje';

// Test verileri yalnızca ana admin tarafından görülebilir
const MAIN_ADMIN_EMAIL = 'turanemre1999@gmail.com';

// isTestProject bazı kayıtlarda boolean, bazılarında 'true' metni olarak
// saklanıyor (bkz. ProjectsManagementPage); iki biçim de kabul ediliyor.
const isTestProjectFlag = (project) =>
  project?.isTestProject === true || project?.isTestProject === 'true';

// "1.234,56" / "1234.56" gibi farklı biçimleri güvenle sayıya çevirir
const toNumber = (value) => {
  if (value === null || value === undefined || value === '') return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  const normalized = String(value)
    .replace(/\s/g, '')
    .replace(/\.(?=\d{3}(\D|$))/g, '')
    .replace(',', '.');
  const parsed = parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

// FTE değerinin parasal karşılığı: tarife değeri x birim fiyat
const calculateAmount = (tariff) => toNumber(tariff.value) * toNumber(tariff.price);

const formatAmount = (amount, currency) => {
  const formatted = new Intl.NumberFormat('tr-TR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount || 0);
  return currency ? `${formatted} ${currency}` : formatted;
};

const compareText = (a, b) =>
  String(a || '').localeCompare(String(b || ''), 'tr', { sensitivity: 'base' });

// Tablo sıralaması: müşteri > proje > tarife tipi > tarife değeri (sayısal),
// eşitlik durumunda en yeni tarih üstte kalır
const compareTariffs = (a, b) => {
  const customerDiff = compareText(a.customerName, b.customerName);
  if (customerDiff !== 0) return customerDiff;

  const projectDiff = compareText(a.projectName, b.projectName);
  if (projectDiff !== 0) return projectDiff;

  const typeDiff = compareText(a.type, b.type);
  if (typeDiff !== 0) return typeDiff;

  const valueDiff = toNumber(a.value) - toNumber(b.value);
  if (valueDiff !== 0) return valueDiff;

  return new Date(b.date || 0) - new Date(a.date || 0);
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  try {
    return format(new Date(dateString), 'dd MMMM yyyy', { locale: tr });
  } catch (error) {
    return dateString;
  }
};

/**
 * Alt Yüklenici / Tarife Yönetimi'nin veri mantığı: müşteri, proje ve tarife
 * okuma, tarifesiz projeler, süzgeçler, silme ve Excel/PDF çıktıları (Türkçe).
 * Eski sayfadaki kod birebir taşındı; bildirimler çevrildi, silme onayı ekrana
 * (ConfirmDialog) alındı ve aktif/pasif sayıları gerçek değerlerden hesaplanıyor.
 */
const useTariffs = () => {
  const { t } = useTranslation();
  // Toasts read the current language through a ref, so switching it never refetches
  const labelRef = useRef(null);
  labelRef.current = (key, vars) => t(`tariffs.toast.${key}`, vars);
  const msg = (key, vars) => labelRef.current(key, vars);
  const { userEmail } = useUserProfile();
  const isMainAdmin =
    String(userEmail || '').trim().toLowerCase() === MAIN_ADMIN_EMAIL;
  const [showTariffModal, setShowTariffModal] = useState(false);
  const [tariffModalKey, setTariffModalKey] = useState(0); // Add key for modal remounting
  const [filteredTariffs, setFilteredTariffs] = useState([]);
  const [filters, setFilters] = useState({});
  const [selectedTariff, setSelectedTariff] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [showProjectsWithoutTariffs, setShowProjectsWithoutTariffs] = useState(false);
  // Test projelerine ait kayıtlar varsayılan olarak gizli; yalnızca ana admin açabilir
  const [showTestProjects, setShowTestProjects] = useState(false);
  const [projectsWithoutTariffs, setProjectsWithoutTariffs] = useState([]);
  const [preselectedCustomer, setPreselectedCustomer] = useState(null);
  const [preselectedProject, setPreselectedProject] = useState(null);

  // Yenilemede ilk sayfaya dönmek için (ekrandaki sayfalama bunu izler)
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [allTariffs, setAllTariffs] = useState([]);
  const [isExporting, setIsExporting] = useState(false);

  // Fetch projects without tariffs
  const fetchProjectsWithoutTariffs = async () => {
    try {
      // Get customers data
      const customersSnapshot = await getDocs(collection(db, 'Customers'));
      const customerIdMap = {};
      customersSnapshot.docs.forEach(doc => {
        customerIdMap[doc.data().Başlık] = doc.id;
      });

      // Get projects data
      const projectsSnapshot = await getDocs(collection(db, 'Projects'));
      const allProjects = [];
      projectsSnapshot.docs.forEach(doc => {
        const projectData = doc.data();
        allProjects.push({
          id: doc.id,
          titleShort: projectData.titleShort,
          customer: projectData.customer,
          customerId: customerIdMap[projectData.customer] || null,
          customerName: projectData.customer,
          isTestProject: isTestProjectFlag(projectData)
        });
      });

      // Get all tariffs
      const allTariffsSnapshot = await getDocs(collection(db, 'Tariffs'));
      const projectIdsWithTariffs = new Set(allTariffsSnapshot.docs.map(doc => doc.data().project));

      // Find projects without tariffs
      const projectsWithoutTariffsData = allProjects.filter(
        project => !projectIdsWithTariffs.has(project.id)
      );
      setProjectsWithoutTariffs(projectsWithoutTariffsData);
    } catch (error) {
      console.error('Error fetching projects without tariffs:', error);
      toast.error(msg('projectsError'));
    }
  };

  // Müşteri, proje ve tarife verisini tek seferde çeker.
  // NOT: orderBy('date') bilerek kullanılmıyor - Firestore, orderBy alanı olmayan
  // dokümanları sonuçtan düşürüyor. Sıralama istemci tarafında yapılıyor.
  const fetchAllTariffs = async () => {
    setIsLoading(true);
    try {
      const [customersSnapshot, projectsSnapshot, tariffsSnapshot] = await Promise.all([
        getDocs(collection(db, 'Customers')),
        getDocs(collection(db, 'Projects')),
        getDocs(collection(db, 'Tariffs')),
      ]);

      const customersMap = {};
      customersSnapshot.docs.forEach(docSnap => {
        customersMap[docSnap.id] = docSnap.data().Başlık || docSnap.data()['Müşteri Adı'];
      });

      const projectsMap = {};
      const testProjectIds = new Set();
      projectsSnapshot.docs.forEach(docSnap => {
        const projectData = docSnap.data();
        projectsMap[docSnap.id] = projectData.titleShort;
        if (isTestProjectFlag(projectData)) testProjectIds.add(docSnap.id);
      });

      const tariffsData = tariffsSnapshot.docs.map(docSnap => {
        const data = docSnap.data();
        // Bazı tarifeler silinmiş projelere işaret ediyor. Eskiden bu durumda
        // ham doküman kimliği ("5j0msgxlk48HdPQlqJUH") proje adı olarak
        // gösteriliyor ve proje listesini kirletiyordu. Artık hepsi tek bir
        // "Silinmiş proje" etiketinde toplanıyor; kayıt gizlenmiyor, yalnızca
        // okunabilir hale geliyor. (Adı tamamen rakamdan oluşan gerçek
        // projeler -- 219867, 223646 gibi -- bundan etkilenmez.)
        const projectTitle = projectsMap[data.project];
        const projectMissing = Boolean(data.project) && !projectTitle;
        const enriched = {
          id: docSnap.id,
          ...data,
          customerName: customersMap[data.customer] || data.customer,
          projectName: projectMissing
            ? DELETED_PROJECT_LABEL
            : (projectTitle || data.project),
          projectMissing,
          isTestProject: testProjectIds.has(data.project),
        };
        // FTE değerinin parasal karşılığı tabloda ve çıktılarda kullanılır
        enriched.amountValue = calculateAmount(enriched);
        enriched.amount = formatAmount(enriched.amountValue, enriched.currency);
        return enriched;
      });

      tariffsData.sort(compareTariffs);

      setAllTariffs(tariffsData);
      return tariffsData;
    } catch (error) {
      console.error('Error fetching tariffs:', error);
      toast.error(msg('loadError'));
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  // Refresh tariffs
  const refreshTariffs = async () => {
    setRefreshNonce((n) => n + 1);
    await fetchAllTariffs();
    await fetchProjectsWithoutTariffs();
  };

  useEffect(() => {
    // Eski sürümden kalan sayfa önbelleği artık kullanılmıyor
    try {
      localStorage.removeItem('sponsorTariffsPageCache');
    } catch (e) {
      // sessizce yoksay
    }

    fetchAllTariffs();
    fetchProjectsWithoutTariffs();
  }, []);

  // Test projelerinin tarifeleri listeden tamamen çıkarılır; yalnızca ana admin
  // "Test Projelerini Göster" düğmesiyle bunları listeye dahil edebilir.
  const visibleTariffs = useMemo(
    () =>
      isMainAdmin && showTestProjects
        ? allTariffs
        : allTariffs.filter(tariff => !tariff.isTestProject),
    [allTariffs, isMainAdmin, showTestProjects]
  );

  const totalTariffs = visibleTariffs.length;

  const visibleProjectsWithoutTariffs = useMemo(
    () =>
      isMainAdmin && showTestProjects
        ? projectsWithoutTariffs
        : projectsWithoutTariffs.filter(project => !project.isTestProject),
    [projectsWithoutTariffs, isMainAdmin, showTestProjects]
  );

  useEffect(() => {
    const hasActiveSearch = searchTerm && searchTerm.trim().length >= 3;
    const hasActiveFilters = Object.keys(filters).some(key => {
      if (filters[key] && typeof filters[key] === 'string' &&
          key !== 'currency' && key !== 'isSubcontractor') {
        return filters[key].trim().length >= 3;
      }
      return filters[key] && filters[key] !== '';
    });

    setIsFilterActive(Boolean(hasActiveSearch || hasActiveFilters || projectFilter));

    // Kaynak her zaman görüntülenebilir tarife listesidir
    let filtered = visibleTariffs;

    // Proje listesinden seçim (tam eşleşme)
    if (projectFilter) {
      filtered = filtered.filter(tariff => tariff.projectName === projectFilter);
    }

    // Apply search term first (only if 3+ characters)
    if (hasActiveSearch) {
      const lowercasedSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(
        tariff =>
          (tariff.customerName && tariff.customerName.toString().toLowerCase().includes(lowercasedSearch)) ||
          (tariff.projectName && tariff.projectName.toString().toLowerCase().includes(lowercasedSearch)) ||
          (tariff.type && tariff.type.toString().toLowerCase().includes(lowercasedSearch)) ||
          (tariff.value && tariff.value.toString().toLowerCase().includes(lowercasedSearch)) ||
          (tariff.info && tariff.info.toString().toLowerCase().includes(lowercasedSearch))
      );
    }

    // Then apply individual column filters
    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        // For text filters, apply 3-character minimum (except select filters)
        if (typeof filters[key] === 'string' &&
            key !== 'currency' &&
            key !== 'isSubcontractor') {
          if (filters[key].trim().length >= 3) {
            filtered = filtered.filter(tariff =>
              String(tariff[key] || '')
                .toLowerCase()
                .includes(filters[key].toLowerCase())
            );
          }
        } else {
          // For select filters, apply immediately
          filtered = filtered.filter(tariff =>
            String(tariff[key] || '')
              .toLowerCase()
              .includes(filters[key].toLowerCase())
          );
        }
      }
    });

    setFilteredTariffs(filtered);
  }, [visibleTariffs, filters, searchTerm, projectFilter]);

  // Listbox için benzersiz proje adları
  const projectOptions = useMemo(() => {
    const names = new Set();
    visibleTariffs.forEach(tariff => {
      if (tariff.projectName) names.add(String(tariff.projectName));
    });
    // "Silinmiş proje" gerçek bir proje adı değil; alfabetik sıraya karışmasın
    // diye listenin sonuna alınıyor.
    return [...names].sort((a, b) => {
      if (a === DELETED_PROJECT_LABEL) return 1;
      if (b === DELETED_PROJECT_LABEL) return -1;
      return a.localeCompare(b, 'tr');
    });
  }, [visibleTariffs]);

  // Filtrelenmiş listenin toplam parasal karşılığı (döviz bazında)
  const amountTotals = useMemo(() => {
    const totals = {};
    filteredTariffs.forEach(tariff => {
      const currency = tariff.currency || '-';
      totals[currency] = (totals[currency] || 0) + (tariff.amountValue || 0);
    });
    return totals;
  }, [filteredTariffs]);

  const handleFilter = (column, value) => {
    const newFilters = { ...filters, [column]: value };
    setFilters(newFilters);
  };

  const clearFilters = () => {
    setFilters({});
    setSearchTerm('');
    setProjectFilter('');
    setIsFilterActive(false);
  };

  // Pasif = FTE düzenlemesinde kaldırılan değerler (active: false); eskiden
  // kartlar tümünü aktif sayıyordu
  const inactiveCount = visibleTariffs.filter((tariff) => tariff.active === false).length;
  const activeCount = totalTariffs - inactiveCount;

  const handleEdit = (tariff) => {
    setSelectedTariff(tariff);
    setTariffModalKey(prev => prev + 1); // Increment key to force remount
    setShowTariffModal(true);
  };

  // Onay ekranda sorulur (ConfirmDialog); burada yalnızca silme yapılır
  const handleDelete = async (id) => {
    try {
      await deleteDoc(doc(db, 'Tariffs', id));
      toast.success(msg('deleted'));
      await refreshTariffs();
    } catch (error) {
      console.error('Error deleting tariff:', error);
      toast.error(msg('deleteError'));
    }
  };

  // Çıktılarda kullanılacak veri: filtre/arama aktifse görünen sonuçlar,
  // değilse tüm liste
  const getExportData = () => (isFilterActive ? filteredTariffs : visibleTariffs);

  const exportFileName = (extension) => {
    const scope = projectFilter ? `_${projectFilter.replace(/[^\w-]+/g, '_')}` : '';
    const today = new Date().toISOString().split('T')[0];
    return `Tarifeler${scope}_${today}.${extension}`;
  };

  const EXPORT_COLUMNS = [
    { key: 'customerName', header: 'Müşteri', width: 22 },
    { key: 'projectName', header: 'Proje', width: 22 },
    { key: 'type', header: 'Tarife Tipi', width: 14 },
    { key: 'value', header: 'Tarife Değer', width: 14 },
    { key: 'price', header: 'Birim Fiyat', width: 14 },
    { key: 'amount', header: 'Tutar', width: 16 },
    { key: 'currency', header: 'Döviz Tipi', width: 12 },
    { key: 'date', header: 'Tarih', width: 18 },
    { key: 'isSubcontractor', header: 'Subcontractor', width: 15 },
    { key: 'info', header: 'Bilgi', width: 30 },
  ];

  const exportRowValue = (tariff, key) => {
    if (key === 'date') return formatDate(tariff.date);
    if (key === 'amount') return tariff.amountValue || 0;
    if (key === 'price' || key === 'value') return toNumber(tariff[key]);
    const value = tariff[key];
    return value === null || value === undefined || value === '' ? '-' : String(value);
  };

  const downloadExcel = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const dataToExport = getExportData();
      if (dataToExport.length === 0) {
        toast.info(msg('nothingToExport'));
        return;
      }

      const ExcelJS = (await import('exceljs')).default;
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Tarifeler');

      worksheet.columns = EXPORT_COLUMNS.map(col => ({
        header: col.header,
        key: col.key,
        width: col.width,
      }));

      dataToExport.forEach(tariff => {
        const row = {};
        EXPORT_COLUMNS.forEach(col => {
          row[col.key] = exportRowValue(tariff, col.key);
        });
        worksheet.addRow(row);
      });

      // Tutar ve fiyat sütunları sayısal biçimlendirilir
      worksheet.getColumn('amount').numFmt = '#,##0.00';
      worksheet.getColumn('price').numFmt = '#,##0.00';

      // Döviz bazında toplam satırları
      worksheet.addRow({});
      Object.entries(
        dataToExport.reduce((acc, tariff) => {
          const currency = tariff.currency || '-';
          acc[currency] = (acc[currency] || 0) + (tariff.amountValue || 0);
          return acc;
        }, {})
      ).forEach(([currency, total]) => {
        const totalRow = worksheet.addRow({
          currency,
          amount: total,
          projectName: `TOPLAM (${currency})`,
        });
        totalRow.font = { bold: true };
      });

      worksheet.getRow(1).eachCell((cell) => {
        cell.font = { bold: true, color: { argb: 'FFE5E7EB' } };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF111827' }
        };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = exportFileName('xlsx');
      a.click();
      window.URL.revokeObjectURL(url);

      toast.success(msg('excelDone'));
    } catch (error) {
      console.error('Error downloading Excel file:', error);
      toast.error(msg('excelError'));
    } finally {
      setIsExporting(false);
    }
  };

  const downloadPdf = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const dataToExport = getExportData();
      if (dataToExport.length === 0) {
        toast.info(msg('nothingToExport'));
        return;
      }

      const { jsPDF } = await import('jspdf');
      const { default: autoTable } = await import('jspdf-autotable');
      const { registerTurkishFont, PDF_FONT_NAME } = await import('../../utils/pdfFont');

      const pdfDoc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
      const hasTurkishFont = await registerTurkishFont(pdfDoc);
      const fontName = hasTurkishFont ? PDF_FONT_NAME : 'helvetica';

      pdfDoc.setFont(fontName, 'normal');
      pdfDoc.setFontSize(14);
      pdfDoc.text('Alt Yüklenici / Tarife Listesi', 40, 40);

      pdfDoc.setFontSize(9);
      const scopeLine = projectFilter ? `Proje: ${projectFilter}` : 'Proje: Tümü';
      pdfDoc.text(
        `${scopeLine}   |   Kayıt: ${dataToExport.length}   |   Tarih: ${new Date().toLocaleDateString('tr-TR')}`,
        40,
        58
      );

      autoTable(pdfDoc, {
        startY: 72,
        head: [EXPORT_COLUMNS.map(col => col.header)],
        body: dataToExport.map(tariff =>
          EXPORT_COLUMNS.map(col =>
            col.key === 'amount'
              ? formatAmount(tariff.amountValue, '')
              : String(exportRowValue(tariff, col.key))
          )
        ),
        styles: { font: fontName, fontSize: 7, cellPadding: 3 },
        headStyles: { font: fontName, fillColor: [17, 24, 39], textColor: [229, 231, 235] },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        columnStyles: {
          4: { halign: 'right' },
          5: { halign: 'right' },
        },
        margin: { left: 40, right: 40 },
      });

      // Döviz bazında toplamlar
      const totals = dataToExport.reduce((acc, tariff) => {
        const currency = tariff.currency || '-';
        acc[currency] = (acc[currency] || 0) + (tariff.amountValue || 0);
        return acc;
      }, {});

      let cursorY = (pdfDoc.lastAutoTable?.finalY || 72) + 20;
      pdfDoc.setFontSize(10);
      Object.entries(totals).forEach(([currency, total]) => {
        pdfDoc.text(`TOPLAM (${currency}): ${formatAmount(total, currency)}`, 40, cursorY);
        cursorY += 14;
      });

      pdfDoc.save(exportFileName('pdf'));
      toast.success(msg('pdfDone'));
    } catch (error) {
      console.error('Error downloading PDF file:', error);
      toast.error(msg('pdfError'));
    } finally {
      setIsExporting(false);
    }
  };

  return {
    isMainAdmin, isLoading, isExporting, refreshNonce,
    allTariffs, visibleTariffs, filteredTariffs, totalTariffs, activeCount, inactiveCount, amountTotals,
    projectOptions, projectsWithoutTariffs: visibleProjectsWithoutTariffs,
    filters, handleFilter, clearFilters, searchTerm, setSearchTerm, projectFilter, setProjectFilter, isFilterActive,
    showTestProjects, setShowTestProjects, showProjectsWithoutTariffs, setShowProjectsWithoutTariffs,
    selectedTariff, setSelectedTariff, handleEdit, handleDelete,
    showTariffModal, setShowTariffModal, tariffModalKey, setTariffModalKey,
    preselectedCustomer, setPreselectedCustomer, preselectedProject, setPreselectedProject,
    refreshTariffs, downloadExcel, downloadPdf,
  };
};

export { DELETED_PROJECT_LABEL, formatAmount, toNumber };
export default useTariffs;
